import React, { useState, useEffect, useRef, useCallback } from 'react';import { useNavigate, useParams, Link } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Youtube from '@tiptap/extension-youtube';
import { Node } from '@tiptap/core';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { postsAPI } from '../api';
import MediaPicker from '../components/MediaPicker';
import { ArrowLeft, Save, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading1, Heading2, Heading3, Link as LinkIcon, Image as ImageIcon, Images as ImagesIcon, Upload, Video, X, ExternalLink, ShieldOff, Sparkles, Loader2, Library, Search, CalendarPlus, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom block-level node for WordPress-style image galleries. Renders as
// <div class="wp-block-gallery"> ... </div> and round-trips through the
// editor save cycle without StarterKit stripping the wrapper.
const Gallery = Node.create({
  name: 'gallery',
  group: 'block',
  content: 'galleryFigure+',
  defining: true,
  parseHTML() {
    return [{ tag: 'div.wp-block-gallery' }, { tag: 'figure.wp-block-gallery' }];
  },
  renderHTML() {
    return ['div', { class: 'wp-block-gallery' }, 0];
  },
});

// Each item in the gallery wraps exactly one image.
const GalleryFigure = Node.create({
  name: 'galleryFigure',
  group: 'block',
  content: 'image',
  defining: true,
  parseHTML() {
    return [{ tag: 'figure.wp-block-image' }];
  },
  renderHTML() {
    return ['figure', { class: 'wp-block-image' }, 0];
  },
});

// Slugify helper — mirrors backend logic so the live preview matches what
// will actually be saved.
const slugify = (text) => (text || '')
  .toLowerCase()
  .trim()
  .replace(/['"]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '') || 'post';

const PostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category: 'news',
    published: true,
    scheduled_at: '',
    meta_title: '',
    meta_description: '',
  });
  // Track whether the admin has manually edited the slug so we don't
  // overwrite their value when the title changes.
  const [slugTouched, setSlugTouched] = useState(false);
  // Alternative headlines from the St Mirren auto-drafter, if this is one.
  const [headlineOptions, setHeadlineOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(isEditMode);
  const [uploading, setUploading] = useState(false);
  const [postLoaded, setPostLoaded] = useState(false);
  const fileInputRef = useRef(null);

  // Media picker state — single instance reused for featured + inline inserts
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState('featured'); // 'featured' | 'editor'

  // AI Draft modal state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiKeywords, setAiKeywords] = useState('');
  const [aiGenerateImage, setAiGenerateImage] = useState(true);
  const [aiStatus, setAiStatus] = useState('idle');  // idle | queued | generating_text | generating_image | done | error
  const [aiError, setAiError] = useState('');
  const aiPollRef = useRef(null);

  // ===== "Save as event too" state =====
  const [eventOpen, setEventOpen] = useState(false);
  const [eventExtracting, setEventExtracting] = useState(false);
  const [eventSaving, setEventSaving] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '', start_date: '', end_date: '', venue: '', address: '',
    description: '', image_url: '', category: 'general', featured: false,
  });
  const [eventConfidence, setEventConfidence] = useState(null);
  const [eventError, setEventError] = useState('');
  const [createdEventSlug, setCreatedEventSlug] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Gallery,
      GalleryFigure,
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-cyan-600 underline',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Youtube.configure({
        inline: false,
        HTMLAttributes: {
          class: 'youtube-embed',
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    },
  });

  const loadPost = useCallback(async () => {
    try {
      const response = await postsAPI.getById(id);
      const data = response.data;
      setFormData({
        title: data.title || '',
        slug: data.slug || '',
        excerpt: data.excerpt || '',
        content: data.content || '',
        featured_image: data.featured_image || '',
        category: data.category || 'news',
        published: data.published !== undefined ? data.published : true,
        scheduled_at: data.scheduled_at ? new Date(data.scheduled_at).toISOString().slice(0, 16) : '',
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
      });
      setHeadlineOptions(data.headline_options || []);
      // Loaded slug is the canonical one; treat any further title edits as
      // potentially diverging, so the slug should not auto-overwrite.
      setSlugTouched(true);
      setPostLoaded(true);
      if (editor && data.content) {
        editor.commands.setContent(data.content);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load post",
        variant: "destructive"
      });
    } finally {
      setLoadingPost(false);
    }
  }, [id, editor, toast]);

  useEffect(() => {
    if (isEditMode) {
      loadPost();
    }
  }, [isEditMode, loadPost]);

  // Set editor content once both editor AND post data are available
  useEffect(() => {
    if (editor && postLoaded && formData.content) {
      editor.commands.setContent(formData.content);
    }
  }, [editor, postLoaded, formData.content]);

  // AI draft: start a job + poll until done
  const aiStatusLabel = {
    queued: 'Queued — starting…',
    generating_text: 'Writing your article (~30-60s)…',
    generating_image: 'Generating featured image (~30-40s)…',
  };

  const stopAiPolling = () => {
    if (aiPollRef.current) {
      clearInterval(aiPollRef.current);
      aiPollRef.current = null;
    }
  };

  const startAiDraft = async () => {
    if (!aiTopic.trim() || aiTopic.trim().length < 3) {
      toast({ title: 'Topic required', description: 'Please enter at least 3 characters.', variant: 'destructive' });
      return;
    }
    setAiError('');
    setAiStatus('queued');
    const token = localStorage.getItem('admin_token');
    try {
      const startRes = await axios.post(
        `${BACKEND_URL}/api/admin/ai-draft`,
        { topic: aiTopic, keywords: aiKeywords, generate_image: aiGenerateImage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const jobId = startRes.data.job_id;
      stopAiPolling();
      aiPollRef.current = setInterval(async () => {
        try {
          const r = await axios.get(`${BACKEND_URL}/api/admin/ai-draft/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setAiStatus(r.data.status);
          if (r.data.status === 'done') {
            stopAiPolling();
            const result = r.data.result;
            // Load into the form
            setFormData(prev => ({
              ...prev,
              title: result.title,
              excerpt: result.excerpt,
              category: result.category,
              content: result.content,
              featured_image: result.featured_image || prev.featured_image,
            }));
            if (editor) editor.commands.setContent(result.content);
            toast({
              title: 'Draft ready!',
              description: `${result.content.split(' ').length} words generated${result.featured_image ? ' + featured image' : ''}. Review & save.`,
            });
            setAiOpen(false);
            setAiStatus('idle');
          } else if (r.data.status === 'error') {
            stopAiPolling();
            setAiError(r.data.error || 'Unknown error');
            setAiStatus('error');
          }
        } catch (pollErr) {
          // 404 means the job was already consumed — stop quietly
          if (pollErr.response?.status === 404) {
            stopAiPolling();
          }
        }
      }, 4000);
    } catch (e) {
      setAiError(e.response?.data?.detail || e.message);
      setAiStatus('error');
    }
  };

  useEffect(() => () => stopAiPolling(), []);

  // ===== "Save as event too" handlers =====
  const toLocalInputValue = (iso) => {
    if (!iso) return '';
    try {
      // Convert ISO Z → local "YYYY-MM-DDTHH:mm" suitable for datetime-local input
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return '';
    }
  };

  const openEventDialog = async () => {
    if (!formData.title || !formData.title.trim()) {
      toast({ title: 'Add a post title first', description: 'The extractor needs at least a title to work from.', variant: 'destructive' });
      return;
    }
    setEventOpen(true);
    setEventError('');
    setCreatedEventSlug(null);
    setEventForm({
      title: formData.title, start_date: '', end_date: '', venue: '', address: '',
      description: formData.excerpt || '', image_url: formData.featured_image || '',
      category: 'general', featured: false,
    });
    setEventConfidence(null);
    setEventExtracting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/extract-event`,
        {
          title: formData.title,
          excerpt: formData.excerpt,
          content: formData.content,
          featured_image: formData.featured_image,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = r.data || {};
      setEventForm((f) => ({
        ...f,
        title: d.title || f.title,
        start_date: toLocalInputValue(d.start_date),
        end_date: toLocalInputValue(d.end_date),
        venue: d.venue || '',
        address: d.address || '',
        description: d.description || f.description,
        image_url: d.image_url || f.image_url,
        category: d.category || 'general',
      }));
      setEventConfidence(typeof d.confidence === 'number' ? d.confidence : null);
    } catch (e) {
      setEventError(e.response?.data?.detail || e.message);
    } finally {
      setEventExtracting(false);
    }
  };

  const saveEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.start_date) {
      toast({ title: 'Title and start date are required', variant: 'destructive' });
      return;
    }
    setEventSaving(true);
    setEventError('');
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.post(
        `${BACKEND_URL}/api/events`,
        {
          ...eventForm,
          end_date: eventForm.end_date || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCreatedEventSlug(r.data.slug);
      toast({ title: 'Event created', description: `Now live at /events/${r.data.slug}` });
    } catch (e) {
      setEventError(e.response?.data?.detail || e.message);
    } finally {
      setEventSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        title: formData.title,
        slug: formData.slug || undefined,
        excerpt: formData.excerpt,
        content: formData.content,
        featured_image: formData.featured_image,
        category: formData.category,
        published: formData.scheduled_at ? false : formData.published,
        scheduled_at: formData.scheduled_at || null,
        meta_title: (formData.meta_title || '').trim(),
        meta_description: (formData.meta_description || '').trim(),
      };

      if (isEditMode) {
        await postsAPI.update(id, submitData);
        toast({
          title: "Success",
          description: "Post updated successfully"
        });
      } else {
        await postsAPI.create(submitData);
        toast({
          title: "Success",
          description: "Post created successfully"
        });
      }
      navigate('/admin/posts');
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${isEditMode ? 'update' : 'create'} post`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, target = 'featured') => {
    if (!file) return;
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${BACKEND_URL}/api/upload`, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      const imageUrl = `${BACKEND_URL}${res.data.url}`;
      setFormData(prev => ({ ...prev, featured_image: imageUrl }));
      toast({ title: "Success", description: "Image uploaded successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  // ===== LINK DIALOG STATE =====
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkForm, setLinkForm] = useState({ url: '', nofollow: false, newTab: true });
  const [linkSearchResults, setLinkSearchResults] = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);
  const linkSearchTimerRef = useRef(null);

  const openLinkDialog = () => {
    // Pre-fill with existing link attributes if cursor is on a link
    if (editor.isActive('link')) {
      const attrs = editor.getAttributes('link');
      const rel = attrs.rel || '';
      setLinkForm({
        url: attrs.href || '',
        nofollow: rel.includes('nofollow'),
        newTab: attrs.target === '_blank',
      });
    } else {
      // Pre-seed search from highlighted selection so admin gets instant
      // suggestions for the text they just selected.
      const { from, to } = editor.state.selection;
      const selected = editor.state.doc.textBetween(from, to, ' ').trim();
      setLinkForm({ url: selected && !/^https?:\/\//i.test(selected) ? '' : selected, nofollow: false, newTab: true });
      if (selected && !/^https?:\/\//i.test(selected) && selected.length >= 2) {
        runLinkSearch(selected);
      } else {
        setLinkSearchResults([]);
      }
    }
    setShowLinkDialog(true);
  };

  const runLinkSearch = async (query) => {
    if (!query || query.trim().length < 2 || /^https?:\/\//i.test(query)) {
      setLinkSearchResults([]);
      return;
    }
    setLinkSearching(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.get(`${BACKEND_URL}/api/admin/internal-link-search`, {
        params: { q: query.trim(), limit: 8 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLinkSearchResults(r.data.results || []);
    } catch {
      setLinkSearchResults([]);
    } finally {
      setLinkSearching(false);
    }
  };

  const handleLinkUrlChange = (val) => {
    setLinkForm(f => ({ ...f, url: val }));
    if (linkSearchTimerRef.current) clearTimeout(linkSearchTimerRef.current);
    linkSearchTimerRef.current = setTimeout(() => runLinkSearch(val), 250);
  };

  const applyLink = () => {
    if (!linkForm.url) {
      // Remove link if URL is empty
      editor.chain().focus().unsetLink().run();
    } else {
      const rel = [linkForm.nofollow && 'nofollow', linkForm.newTab && 'noopener noreferrer'].filter(Boolean).join(' ') || undefined;
      editor.chain().focus().setLink({
        href: linkForm.url,
        target: linkForm.newTab ? '_blank' : null,
        rel: rel,
      }).run();
    }
    setShowLinkDialog(false);
    setLinkSearchResults([]);
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
    setShowLinkDialog(false);
    setLinkSearchResults([]);
  };

  // ===== YOUTUBE DIALOG STATE =====
  const [showYouTubeDialog, setShowYouTubeDialog] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');

  const addYouTube = () => {
    if (!youtubeUrl) return;
    editor.commands.setYoutubeVideo({ src: youtubeUrl, width: 640, height: 360 });
    setYoutubeUrl('');
    setShowYouTubeDialog(false);
  };

  if (loadingPost) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!editor) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/posts">
                <Button variant="outline" size="sm">
                  <ArrowLeft size={16} className="mr-2" />
                  Back to Posts
                </Button>
              </Link>
              <h1 className="text-2xl font-bold text-slate-900">
                {isEditMode ? 'Edit Post' : 'Create New Post'}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={openEventDialog}
                variant="outline"
                className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 hover:text-fuchsia-800"
                data-testid="save-as-event-btn"
              >
                <CalendarPlus size={16} className="mr-2" />
                Save as event too
              </Button>
              <Button
                type="button"
                onClick={() => { setAiOpen(true); setAiStatus('idle'); setAiError(''); }}
                className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white shadow-sm"
                data-testid="draft-with-ai-btn"
              >
                <Sparkles size={16} className="mr-2" />
                Draft with AI
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
            <div className="space-y-6">
              {/* Title */}
              <div>
                <Label htmlFor="title" className="text-slate-900 font-medium">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => {
                    const t = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      title: t,
                      // Auto-update slug only when the admin hasn't customised it
                      slug: slugTouched ? prev.slug : slugify(t),
                    }));
                  }}
                  placeholder="Enter post title"
                  required
                  className="mt-2"
                />
                {headlineOptions.length > 1 && (
                  <div className="mt-2" data-testid="headline-options">
                    <p className="text-xs text-slate-500 mb-1.5">
                      Other headlines for this match — click to use one:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {headlineOptions.map((option) => {
                        const active = option === formData.title;
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, title: option }))}
                            className={`text-left text-xs px-2.5 py-1.5 rounded-lg border transition-colors
                              ${active
                                ? 'bg-slate-900 text-white border-slate-900'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50'}`}
                            data-testid={`headline-option-${headlineOptions.indexOf(option)}`}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5">
                      The web address stays as it is, so picking a different headline
                      will not break any link already shared.
                    </p>
                  </div>
                )}
              </div>

              {/* Slug — editable, with live URL preview */}
              <div>
                <Label htmlFor="slug" className="text-slate-900 font-medium">URL Slug</Label>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500 shrink-0 hidden sm:inline">/news/</span>
                  <Input
                    id="slug"
                    data-testid="post-slug-input"
                    value={formData.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setFormData(prev => ({ ...prev, slug: e.target.value }));
                    }}
                    onBlur={() => {
                      // Normalise on blur so what they see matches what's saved
                      setFormData(prev => ({ ...prev, slug: slugify(prev.slug) }));
                    }}
                    placeholder="auto-generated-from-title"
                    className="flex-1"
                  />
                  {slugTouched && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setSlugTouched(false); setFormData(prev => ({ ...prev, slug: slugify(prev.title) })); }}
                      className="shrink-0 text-xs"
                      data-testid="post-slug-reset-btn"
                    >
                      Reset to title
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  Lowercase letters, numbers and dashes only. Leave blank to auto-generate from the title.
                </p>
              </div>

              {/* Category */}
              <div>
                <Label htmlFor="category" className="text-slate-900 font-medium">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="news">News</SelectItem>
                    <SelectItem value="events">Events</SelectItem>
                    <SelectItem value="history">History</SelectItem>
                    <SelectItem value="attractions">Visitor Attractions</SelectItem>
                    <SelectItem value="food-drink">Food &amp; Drink</SelectItem>
                    <SelectItem value="gallery">Photo Gallery</SelectItem>
                    <SelectItem value="people">Famous People</SelectItem>
                    <SelectItem value="announcements">Announcements</SelectItem>
                    <SelectItem value="st-mirren">St Mirren</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Featured Image */}
              <div>
                <Label htmlFor="featured_image" className="text-slate-900 font-medium">Featured Image</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Input
                    id="featured_image"
                    data-testid="featured-image-input"
                    value={formData.featured_image}
                    onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                    placeholder="https://example.com/image.jpg or pick from library"
                    className="flex-1 min-w-[200px]"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'featured')}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="featured-image-library-btn"
                    onClick={() => { setMediaPickerTarget('featured'); setMediaPickerOpen(true); }}
                    className="shrink-0"
                  >
                    <Library size={16} className="mr-2" />
                    Choose from Library
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="upload-featured-image-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="shrink-0"
                  >
                    <Upload size={16} className="mr-2" />
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
                {formData.featured_image && (
                  <img
                    src={formData.featured_image}
                    alt="Preview"
                    data-testid="featured-image-preview"
                    className="mt-4 w-full max-w-md h-48 object-cover rounded-lg"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>

              {/* Excerpt */}
              <div>
                <Label htmlFor="excerpt" className="text-slate-900 font-medium">Excerpt *</Label>
                <textarea
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Brief description of the post"
                  required
                  rows={3}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {/* SEO overrides — optional. When empty, PostDetail falls back
                  to the post title + excerpt. Ideal lengths: title ~55–60
                  chars, description ~150–160 chars. */}
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/60" data-testid="seo-overrides">
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">
                  SEO overrides <span className="normal-case font-normal text-slate-400">— optional, only fill in when you want to override the auto title/description</span>
                </p>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="meta_title" className="text-slate-900 font-medium">Meta title</Label>
                    <input
                      id="meta_title"
                      type="text"
                      maxLength={120}
                      value={formData.meta_title}
                      onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                      placeholder={formData.title || 'Leave blank to use the post title'}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      data-testid="meta-title-input"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">
                      {formData.meta_title.length}/60 chars recommended
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="meta_description" className="text-slate-900 font-medium">Meta description</Label>
                    <textarea
                      id="meta_description"
                      value={formData.meta_description}
                      onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      placeholder={formData.excerpt ? formData.excerpt.slice(0, 160) : 'Leave blank to use the excerpt'}
                      rows={2}
                      maxLength={300}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      data-testid="meta-description-input"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">
                      {formData.meta_description.length}/160 chars recommended
                    </p>
                  </div>
                </div>
              </div>

              {/* Content (TipTap Editor) */}
              <div>
                <Label className="text-slate-900 font-medium mb-2 block">Content *</Label>
                
                {/* Toolbar */}
                <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-2 flex flex-wrap gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={editor.isActive('bold') ? 'bg-gray-200' : ''}
                  >
                    <Bold size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={editor.isActive('italic') ? 'bg-gray-200' : ''}
                  >
                    <Italic size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    className={editor.isActive('underline') ? 'bg-gray-200' : ''}
                  >
                    <UnderlineIcon size={16} />
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1"></div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    className={editor.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''}
                  >
                    <Heading1 size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}
                  >
                    <Heading2 size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}
                  >
                    <Heading3 size={16} />
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1"></div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={editor.isActive('bulletList') ? 'bg-gray-200' : ''}
                  >
                    <List size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={editor.isActive('orderedList') ? 'bg-gray-200' : ''}
                  >
                    <ListOrdered size={16} />
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1"></div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openLinkDialog}
                    className={editor.isActive('link') ? 'bg-blue-100 border-blue-300' : ''}
                    title="Insert / Edit Link"
                    data-testid="toolbar-link-btn"
                  >
                    <LinkIcon size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setMediaPickerTarget('editor'); setMediaPickerOpen(true); }}
                    title="Insert image from library or upload"
                    data-testid="toolbar-image-btn"
                  >
                    <ImageIcon size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setMediaPickerTarget('gallery'); setMediaPickerOpen(true); }}
                    title="Insert a gallery of multiple images"
                    data-testid="toolbar-gallery-btn"
                  >
                    <ImagesIcon size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setYoutubeUrl(''); setShowYouTubeDialog(true); }}
                    title="Embed YouTube Video"
                    data-testid="toolbar-youtube-btn"
                  >
                    <Video size={16} />
                  </Button>
                </div>

                {/* Editor */}
                <div className="border border-t-0 border-gray-300 rounded-b-lg min-h-[400px] p-4 bg-white prose max-w-none">
                  <EditorContent editor={editor} />
                </div>

                {/* Link Dialog */}
                {showLinkDialog && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLinkDialog(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="link-dialog">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-slate-900">Insert / Edit Link</h3>
                        <button onClick={() => setShowLinkDialog(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">URL or search internal posts</Label>
                          <div className="relative mt-1">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <Input
                              value={linkForm.url}
                              onChange={e => handleLinkUrlChange(e.target.value)}
                              placeholder="https://… or 'paisley town hall'"
                              className="mt-0 pl-9"
                              data-testid="link-url-input"
                              autoFocus
                            />
                          </div>
                          {/* Internal link suggestions */}
                          {(linkSearching || linkSearchResults.length > 0) && (
                            <div className="mt-2 border border-gray-200 rounded-lg max-h-56 overflow-y-auto bg-slate-50" data-testid="link-search-results">
                              {linkSearching && (
                                <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
                                  <Loader2 size={12} className="animate-spin" /> Searching internal posts…
                                </div>
                              )}
                              {!linkSearching && linkSearchResults.map((r) => (
                                <button
                                  type="button"
                                  key={r.slug}
                                  onClick={() => setLinkForm(f => ({ ...f, url: r.url }))}
                                  className="w-full text-left px-3 py-2 hover:bg-cyan-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                  data-testid="link-search-result"
                                >
                                  <p className="text-sm text-slate-900 line-clamp-1">{r.title}</p>
                                  <p className="text-[11px] text-cyan-600 font-mono truncate">{r.url}</p>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={linkForm.newTab}
                              onChange={e => setLinkForm(f => ({ ...f, newTab: e.target.checked }))}
                              className="w-4 h-4 text-cyan-500 border-gray-300 rounded"
                              data-testid="link-newtab-checkbox"
                            />
                            <ExternalLink size={14} className="text-gray-500" />
                            <span className="text-sm text-slate-700">Open in new tab</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={linkForm.nofollow}
                              onChange={e => setLinkForm(f => ({ ...f, nofollow: e.target.checked }))}
                              className="w-4 h-4 text-cyan-500 border-gray-300 rounded"
                              data-testid="link-nofollow-checkbox"
                            />
                            <ShieldOff size={14} className="text-gray-500" />
                            <span className="text-sm text-slate-700">No-follow</span>
                          </label>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button type="button" onClick={applyLink} className="bg-cyan-500 hover:bg-cyan-600 flex-1" data-testid="link-apply-btn">
                            Apply Link
                          </Button>
                          {editor.isActive('link') && (
                            <Button type="button" variant="outline" onClick={removeLink} className="text-red-500 border-red-300 hover:bg-red-50" data-testid="link-remove-btn">
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* YouTube Dialog */}
                {showYouTubeDialog && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowYouTubeDialog(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="youtube-dialog">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-slate-900">Embed YouTube Video</h3>
                        <button onClick={() => setShowYouTubeDialog(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">YouTube URL</Label>
                          <Input
                            value={youtubeUrl}
                            onChange={e => setYoutubeUrl(e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=..."
                            className="mt-1"
                            data-testid="youtube-url-input"
                            autoFocus
                          />
                          <p className="text-xs text-gray-400 mt-1">Paste any YouTube URL — full link, short link, or embed URL</p>
                        </div>
                        <Button type="button" onClick={addYouTube} className="bg-red-500 hover:bg-red-600 w-full" data-testid="youtube-embed-btn" disabled={!youtubeUrl}>
                          <Video size={16} className="mr-2" />
                          Embed Video
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Published Status */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published && !formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked, scheduled_at: e.target.checked ? '' : formData.scheduled_at })}
                    disabled={!!formData.scheduled_at}
                    className="w-4 h-4 text-cyan-500 border-gray-300 rounded focus:ring-cyan-500"
                  />
                  <Label htmlFor="published" className="text-slate-900 font-medium cursor-pointer">
                    Publish immediately
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-slate-700 text-sm shrink-0">Schedule for:</Label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value, published: e.target.value ? false : formData.published })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-cyan-500 focus:border-cyan-500"
                    data-testid="schedule-input"
                  />
                  {formData.scheduled_at && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, scheduled_at: '', published: true })}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Clear schedule
                    </button>
                  )}
                </div>
                {formData.scheduled_at && (
                  <p className="text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg">
                    This post will auto-publish at {new Date(formData.scheduled_at).toLocaleString()}. It stays as a draft until then.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <Button
              type="submit"
              className="bg-cyan-500 hover:bg-cyan-600 px-8 py-6"
              disabled={loading}
            >
              <Save size={18} className="mr-2" />
              {loading ? 'Saving...' : (isEditMode ? 'Update Post' : 'Create Post')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/posts')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>

      {/* AI Draft Modal */}
      <Dialog open={aiOpen} onOpenChange={(o) => { if (aiStatus === 'idle' || aiStatus === 'error' || aiStatus === 'done') setAiOpen(o); }}>
        <DialogContent className="sm:max-w-xl" data-testid="ai-draft-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={18} className="text-violet-500" />
              Draft a Paisley.org.uk article with AI
            </DialogTitle>
            <DialogDescription>
              Claude Sonnet 4.5 will draft a 1200–1500 word SEO-optimized article in your house voice,
              with H2/H3 structure, internal links, and a featured image (Gemini Nano Banana). You can
              edit everything afterwards before publishing.
            </DialogDescription>
          </DialogHeader>

          {(aiStatus === 'idle' || aiStatus === 'error') && (
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="ai-topic" className="text-slate-900 font-medium">Topic *</Label>
                <Textarea
                  id="ai-topic"
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  placeholder="e.g. A guide to visiting Paisley Abbey in 2026 — opening hours, tours, and what to see."
                  className="mt-2 min-h-[80px]"
                  data-testid="ai-topic-input"
                />
              </div>
              <div>
                <Label htmlFor="ai-keywords" className="text-slate-900 font-medium">Focus keywords (optional)</Label>
                <Input
                  id="ai-keywords"
                  value={aiKeywords}
                  onChange={(e) => setAiKeywords(e.target.value)}
                  placeholder="e.g. Paisley Abbey, tourist guide, opening hours"
                  className="mt-2"
                  data-testid="ai-keywords-input"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Comma-separated. The AI front-loads these in the title, first H2, and first paragraph.
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={aiGenerateImage}
                  onChange={(e) => setAiGenerateImage(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-violet-500 focus:ring-violet-500"
                  data-testid="ai-image-checkbox"
                />
                <span className="text-sm text-slate-700">
                  Also generate featured image (adds ~30–40s)
                </span>
              </label>
              {aiGenerateImage && /\b(paisley abbey|paisley museum|paisley town hall|thomas coats memorial|coats memorial|russell institute|sma shot|anchor mills|paisley arts centre|paisley central library|paisley grammar school|paisley canal|paisley cenotaph|stanely castle|fountain gardens|barshaw park|lagoon leisure|paisley war memorial|oakshaw trinity|st mirin|coats observatory|paisley town centre|high street paisley|paisley cross|ferguslie|the bungalow|paisley harbour|paisley piazza|paisley railway station|paisley gilmour street|paisley canal station|love street|st mirren|kelburne cinema|glen cinema|the swan paisley)\b/i.test(aiTopic) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs" data-testid="single-landmark-notice">
                  <p className="font-semibold text-amber-900 mb-1">⚠ Real-photo policy: AI image will be skipped</p>
                  <p className="text-amber-800 leading-relaxed">
                    Your topic mentions a real Paisley landmark. We never AI-generate images of real buildings
                    — please upload an authentic photo in the editor after generating.
                  </p>
                </div>
              )}
              {aiStatus === 'error' && (
                <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg p-3" data-testid="ai-error">
                  <strong>Generation failed:</strong> {aiError}
                  {/budget/i.test(aiError || '') && (
                    <p className="mt-2 text-xs text-red-600">
                      Your Emergent Universal Key balance is exhausted. Top up via
                      <strong> Profile → Universal Key → Add Balance</strong> (or enable auto top-up), then try again.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {(aiStatus === 'queued' || aiStatus === 'generating_text' || aiStatus === 'generating_image') && (
            <div className="py-8 flex flex-col items-center justify-center gap-4 text-center" data-testid="ai-loading">
              <Loader2 size={36} className="animate-spin text-violet-500" />
              <p className="text-sm font-medium text-slate-800">{aiStatusLabel[aiStatus] || 'Working…'}</p>
              <p className="text-xs text-gray-500 max-w-sm">
                Don't close this dialog — your article is being generated and will load directly into the editor when ready.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            {(aiStatus === 'idle' || aiStatus === 'error') && (
              <>
                <Button variant="outline" onClick={() => setAiOpen(false)} data-testid="ai-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={startAiDraft}
                  className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600"
                  data-testid="ai-generate-btn"
                >
                  <Sparkles size={14} className="mr-2" />
                  Generate draft
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Save as event too" dialog */}
      <Dialog open={eventOpen} onOpenChange={(o) => { if (!eventSaving && !eventExtracting) setEventOpen(o); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="save-as-event-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus size={18} className="text-fuchsia-500" />
              Save this post as an event too
            </DialogTitle>
            <DialogDescription>
              Claude reads your post and pre-fills an event entry. Edit anything that's off, then save —
              the event will appear on the public calendar and get its own SEO page at <code className="text-cyan-600">/events/&#123;slug&#125;</code>.
            </DialogDescription>
          </DialogHeader>

          {eventExtracting ? (
            <div className="py-12 flex flex-col items-center gap-3" data-testid="event-extracting">
              <Loader2 size={32} className="animate-spin text-fuchsia-500" />
              <p className="text-sm font-medium text-slate-800">Reading your post for event details…</p>
              <p className="text-xs text-gray-500">Claude is extracting date, venue, category and description.</p>
            </div>
          ) : createdEventSlug ? (
            <div className="py-8 text-center space-y-3" data-testid="event-created">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                <CalendarPlus size={22} className="text-emerald-600" />
              </div>
              <p className="text-lg font-semibold text-slate-900">Event created</p>
              <p className="text-sm text-gray-600">
                Live at <a href={`/events/${createdEventSlug}`} target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline">/events/{createdEventSlug}</a>.
              </p>
              <div className="flex gap-2 justify-center pt-2">
                <Button type="button" variant="outline" onClick={() => setEventOpen(false)}>Close</Button>
                <Button type="button" onClick={() => navigate('/admin/events')} className="bg-cyan-500 hover:bg-cyan-600">
                  Go to Events Manager
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {eventConfidence !== null && (
                <div className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${
                  eventConfidence >= 0.7 ? 'bg-emerald-50 text-emerald-700' :
                  eventConfidence >= 0.4 ? 'bg-amber-50 text-amber-700' :
                  'bg-red-50 text-red-700'
                }`} data-testid="event-confidence">
                  <Sparkles size={12} />
                  Extraction confidence: <strong>{Math.round(eventConfidence * 100)}%</strong>
                  {eventConfidence < 0.4 && ' — Claude is unsure this post is an event. Double-check the date and venue before saving.'}
                </div>
              )}

              <div>
                <Label className="text-sm">Event title *</Label>
                <Input
                  value={eventForm.title}
                  onChange={(e) => setEventForm((f) => ({ ...f, title: e.target.value }))}
                  className="mt-1"
                  data-testid="event-modal-title"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Start date/time *</Label>
                  <Input
                    type="datetime-local"
                    value={eventForm.start_date}
                    onChange={(e) => setEventForm((f) => ({ ...f, start_date: e.target.value }))}
                    className="mt-1"
                    data-testid="event-modal-start"
                  />
                </div>
                <div>
                  <Label className="text-sm">End date/time</Label>
                  <Input
                    type="datetime-local"
                    value={eventForm.end_date}
                    onChange={(e) => setEventForm((f) => ({ ...f, end_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm flex items-center gap-1"><MapPin size={12} />Venue</Label>
                  <Input
                    value={eventForm.venue}
                    onChange={(e) => setEventForm((f) => ({ ...f, venue: e.target.value }))}
                    placeholder="e.g. Paisley Town Hall"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Address</Label>
                  <Input
                    value={eventForm.address}
                    onChange={(e) => setEventForm((f) => ({ ...f, address: e.target.value }))}
                    placeholder="Street, postcode"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm">Short description</Label>
                <Textarea
                  rows={3}
                  value={eventForm.description}
                  onChange={(e) => setEventForm((f) => ({ ...f, description: e.target.value }))}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Category</Label>
                  <Select value={eventForm.category} onValueChange={(v) => setEventForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['general','music','family','sport','food','community','festival','theatre','market','charity'].map((c) =>
                        <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer pb-2">
                    <input
                      type="checkbox"
                      checked={eventForm.featured}
                      onChange={(e) => setEventForm((f) => ({ ...f, featured: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Feature on homepage</span>
                  </label>
                </div>
              </div>

              {eventForm.image_url && (
                <div>
                  <Label className="text-sm">Event image (from post)</Label>
                  <img
                    src={eventForm.image_url.startsWith('http') ? eventForm.image_url : `${BACKEND_URL}${eventForm.image_url}`}
                    alt=""
                    className="mt-1 w-full h-32 object-cover rounded-lg"
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                </div>
              )}

              {eventError && (
                <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg p-3" data-testid="event-modal-error">
                  <strong>Couldn't save:</strong> {eventError}
                </div>
              )}
            </div>
          )}

          {!eventExtracting && !createdEventSlug && (
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEventOpen(false)} disabled={eventSaving}>
                Cancel
              </Button>
              <Button
                onClick={saveEvent}
                disabled={eventSaving || !eventForm.title || !eventForm.start_date}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="event-modal-save-btn"
              >
                {eventSaving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CalendarPlus size={14} className="mr-2" />}
                {eventSaving ? 'Saving…' : 'Create event'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Media Library Picker — used for both featured image + inline editor inserts */}
      <MediaPicker
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        multi={mediaPickerTarget === 'gallery'}
        aiContext={{
          kind: 'post',
          title: formData.title,
          context: `${formData.excerpt || ''}\n\n${(formData.content || '').replace(/<[^>]+>/g, ' ').slice(0, 4000)}`,
        }}
        title={
          mediaPickerTarget === 'featured' ? 'Select featured image'
          : mediaPickerTarget === 'gallery' ? 'Select images for gallery'
          : 'Insert image into content'
        }
        onSelect={(value) => {
          if (mediaPickerTarget === 'featured') {
            setFormData(prev => ({ ...prev, featured_image: value }));
            toast({ title: 'Featured image set' });
          } else if (mediaPickerTarget === 'gallery' && editor && Array.isArray(value)) {
            // Build a wp-block-gallery HTML block so our reader-side CSS grid + lightbox kick in
            const html = `<div class="wp-block-gallery">${
              value.map((u) => `<figure class="wp-block-image"><img src="${u}" alt="" /></figure>`).join('')
            }</div><p></p>`;
            editor.chain().focus().insertContent(html).run();
            toast({ title: `${value.length} images inserted as gallery` });
          } else if (editor) {
            editor.chain().focus().setImage({ src: value }).run();
            toast({ title: 'Image inserted' });
          }
        }}
      />
    </div>
  );
};

export default PostEditor;
